import type { DateRange, GlobalFilter, ShowFilter } from '@/types';

/**
 * Default show selection for the earnings download drawer.
 * Empty means all shows (no showIds query param) — intentional; do not seed from globalFilters.selectedShow.
 */
export const EARNINGS_DOWNLOAD_DEFAULT_SHOW_IDS: string[] = [];

/**
 * Maps global show filter state to show ids for content performance download drawer defaults.
 * Returns an empty array when "all" or no show is selected.
 */
export const getShowIdsFromShowFilter = (selectedShow: ShowFilter): string[] => {
  if (selectedShow === 'all' || selectedShow === undefined) {
    return [];
  }
  return [selectedShow];
};

/**
 * Label for the primary download action, e.g. "Download (2 CSVs)".
 */
export const formatDownloadButtonLabel = (selectedFileCount: number): string =>
  `Download (${selectedFileCount} CSV${selectedFileCount === 1 ? '' : 's'})`;

interface ContentExportPathParams {
  networkIds: string[];
  from: string;
  to: string;
  fileKeys: string[];
  /** Empty means every show across the selected networks. */
  showIds: string[];
}

/**
 * Builds the content performance export request path.
 *
 * Uses the network-agnostic `/v2/content/export?networkIds=a,b` route so the export covers every
 * network in the `?networks=` selection. The legacy `/v2/networks/:networkId/content/export` path
 * is an N=1 adapter, so it silently dropped shows belonging to any network but the primary one.
 */
export const buildContentExportPath = ({
  networkIds,
  from,
  to,
  fileKeys,
  showIds,
}: ContentExportPathParams): string => {
  const searchParams = new URLSearchParams({
    networkIds: networkIds.join(','),
    from,
    to,
    files: fileKeys.join(','),
  });

  if (showIds.length > 0) {
    searchParams.append('showIds', showIds.join(','));
  }

  return `/v2/content/export?${searchParams.toString()}`;
};

export type ScopedContentExportScope = 'episode' | 'show' | 'all';
export type ScopedContentExportBreakdown = 'total' | 'daily' | 'weekly' | 'monthly';
export type ScopedContentPostLaunchComparison = '7-day' | '30-day';

interface ScopedContentExportPathParams {
  networkIds: string[];
  from: string;
  to: string;
  scope: ScopedContentExportScope;
  breakdown: ScopedContentExportBreakdown;
  /** Empty omits the param. Checked windows are included for every breakdown. */
  postLaunchComparison: ScopedContentPostLaunchComparison[];
  /** Empty means every show across the selected networks. */
  showIds: string[];
}

/**
 * Builds the scoped content performance export path.
 *
 * `scope`, `breakdown`, or `postLaunchComparison` selects the scoped contract on
 * `/v2/content/export` instead of the legacy `files` list.
 */
export const buildScopedContentExportPath = ({
  networkIds,
  from,
  to,
  scope,
  breakdown,
  postLaunchComparison,
  showIds,
}: ScopedContentExportPathParams): string => {
  const searchParams = new URLSearchParams({
    networkIds: networkIds.join(','),
    from,
    to,
    scope,
    breakdown,
  });

  if (postLaunchComparison.length > 0) {
    searchParams.append('postLaunchComparison', postLaunchComparison.join(','));
  }

  if (showIds.length > 0) {
    searchParams.append('showIds', showIds.join(','));
  }

  return `/v2/content/export?${searchParams.toString()}`;
};

export type DownloadPeriod = 'all' | 'month' | 'week' | 'day';

export const DOWNLOAD_PERIOD_OPTIONS: { label: string; value: DownloadPeriod }[] = [
  { label: 'Total for period', value: 'all' },
  { label: 'Monthly totals', value: 'month' },
  { label: 'Weekly totals', value: 'week' },
  { label: 'Daily totals', value: 'day' },
];

/** The period options, with everything but the total disabled when the range is over a year. */
export const getPeriodOptions = (
  isRangeRestricted: boolean
): { label: string; value: DownloadPeriod; disabled: boolean }[] =>
  DOWNLOAD_PERIOD_OPTIONS.map((option) => ({
    ...option,
    disabled: isRangeRestricted && option.value !== 'all',
  }));

/** Maps the drawer period options onto the export `breakdown` query value. */
export const BREAKDOWN_BY_PERIOD: Record<DownloadPeriod, ScopedContentExportBreakdown> = {
  all: 'total',
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
};

/**
 * Seeds show and date fields from the page filters.
 * An empty `selectedShows` means all shows.
 */
export const showsAndDateFromGlobalFilters = (
  globalFilters: GlobalFilter
): { shows: string[]; dateRange: DateRange } => ({
  shows: globalFilters.selectedShows,
  dateRange: globalFilters.dateRange,
});

export const AUDIENCE_REPORT_KEYS = ['uniques', 'platforms', 'location', 'device'] as const;
export type AudienceReportKey = (typeof AUDIENCE_REPORT_KEYS)[number];

interface AudienceExportPathParams {
  networkId: string;
  from: string;
  to: string;
  breakdown: ScopedContentExportBreakdown;
  /** Checked reports, in {@link AUDIENCE_REPORT_KEYS} order. */
  reports: AudienceReportKey[];
  /** Empty means every show on the network. */
  showIds: string[];
  /** Release flag to send as `flag=<name>`, from `useReleaseFlag`. */
  flag?: string;
}

/**
 * Builds the audience export request path for the current network.
 *
 * The API returns one CSV when a single report is selected, and a ZIP when more than one is.
 */
export const buildAudienceExportPath = ({
  networkId,
  from,
  to,
  breakdown,
  reports,
  showIds,
  flag,
}: AudienceExportPathParams): string => {
  const searchParams = new URLSearchParams({
    networkIds: networkId,
    from,
    to,
    breakdown,
    reports: reports.join(','),
  });

  if (showIds.length > 0) {
    searchParams.append('showIds', showIds.join(','));
  }

  if (flag) {
    searchParams.append('flag', flag);
  }

  return `/v2/audience/export?${searchParams.toString()}`;
};
